import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../utils/rtk-query';
import S3DocumentController from '../s3DocumentController';

export interface CSVUploadRequest {
  crmNumber: string;
  files: {
    trucks?: File;
    trailers?: File;
    drivers?: File;
    loads?: File;
    fuel?: File;
  };
  transactionId?: string;
}

export interface CSVUploadResponse {
  success: boolean;
  uploadedFiles: string[];
  transactionId: string;
  errors?: string[];
}

export interface CompanyResetRequest {
  crmNumber: string;
  dataOption: 1 | 2 | 3; // 1 - Empty, 2 - CSV Import, 3 - Mock Data
  transactionId?: string;
  csvFileKeys?: string[];
  mockData?: {
    trucks: number;
    trailers: number;
    drivers: number;
    loads: number;
    fuel: number;
    weeks: number;
  };
}

export interface CompanyResetResponse {
  success: boolean;
  message: string;
}

interface CompanyResetTableResponse {
  crms: string[];
}

export const ptCompanyResetApi = createApi({
  reducerPath: 'ptCompanyResetApi',
  baseQuery: axiosBaseQuery({
    baseUrl: config.ptInternalAdministrationServiceUrl,
  }),
  tagTypes: ['CompanyReset'],
  endpoints: (builder) => ({
    uploadCSVFiles: builder.mutation<CSVUploadResponse, CSVUploadRequest>({
      queryFn: async ({ crmNumber, files, transactionId }) => {
        try {
          const s3Controller = new S3DocumentController();
          const txnId = transactionId || crypto.randomUUID();
          const uploadedFiles: string[] = [];
          const errors: string[] = [];

          for (const [fileType, file] of Object.entries(files)) {
            if (file) {
              try {
                const key = `${crmNumber}-${txnId}/${fileType}/${file.name}`;
                const mimeType = 'text/csv';

                await s3Controller.uploadToStage(key, mimeType, file, undefined, {
                  companyId: crmNumber,
                  name: file.name,
                  getUrl: false,
                });

                uploadedFiles.push(key);
              } catch (error) {
                console.error(`Failed to upload ${fileType} file:`, error);
                errors.push(
                  `Failed to upload ${fileType}: ${error instanceof Error ? error.message : 'Unknown error'}`,
                );
              }
            }
          }

          return {
            data: {
              success: errors.length === 0,
              uploadedFiles,
              transactionId: txnId,
              errors: errors.length > 0 ? errors : undefined,
            },
          };
        } catch (error) {
          console.error('CSV upload process failed:', error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error instanceof Error ? error.message : 'Upload failed',
            },
          };
        }
      },
    }),

    resetCompany: builder.mutation<CompanyResetResponse, CompanyResetRequest>({
      query: (data) => {
        return {
          url: '/company/reset',
          method: 'POST',
          data: {
            crmNumber: data.crmNumber,
            dataOption: data.dataOption,
            ...(data.dataOption === 2 && {
              csvFileKeys: data.csvFileKeys,
            }),
            ...(data.dataOption === 3 && {
              mockData: data.mockData,
            }),
          },
        };
      },
      invalidatesTags: ['CompanyReset'],
    }),
    companyResetTable: builder.query<CompanyResetTableResponse, void>({
      query: () => ({
        url: '/company/resetable',
        method: 'GET',
      }),
    }),
  }),
});

export const { useUploadCSVFilesMutation, useResetCompanyMutation, useCompanyResetTableQuery } = ptCompanyResetApi;
